import { createContext, useContext, useEffect, useState } from "react";
import { Loader2, CheckCircle2, X } from "lucide-react";
import { api } from "@/lib/api";
import { ALL_SLOTS } from "@/constants/timetable";

// One booking form for the whole site: "Book Your First Free Coached Session", "Book Coached Session"
// and every timetable slot open it. Sending it emails the gym's business inbox.
const Ctx = createContext({ open: () => {} });
export const useBooking = () => useContext(Ctx);

const OTHER = "Another day or time (tell us below)";
const BUSINESS_EMAIL = "Elysiumtrainingclub@gmail.com";

export function BookingProvider({ children }) {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", session: ALL_SLOTS[0], preferred: "", website: "" });
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const [err, setErr] = useState("");

  const open = (slot) => {
    setForm((f) => ({ ...f, session: slot && ALL_SLOTS.includes(slot) ? slot : f.session || ALL_SLOTS[0] }));
    setState("idle"); setErr(""); setShow(true);
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!show) return;
    const onKey = (e) => e.key === "Escape" && setShow(false);
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [show]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return setErr("Please enter your full name.");
    if (form.phone.replace(/\D/g, "").length < 7) return setErr("Please enter a mobile number we can reach you on.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return setErr("Please enter a valid email address.");
    if (form.session === OTHER && !form.preferred.trim()) return setErr("Please tell us the day and time you would like.");
    setErr(""); setState("sending");
    try {
      await api.post("/classes/enquiry", { ...form, session: form.session === OTHER ? "Another day or time" : form.session });
      setState("done");
    } catch (ex) {
      if (ex?.response?.status === 429) { setState("done"); return; }
      const detail = ex?.response?.data?.detail;
      if (typeof detail === "string" && ex?.response?.status === 400) { setErr(detail); setState("idle"); return; }
      setState("error");
    }
  };

  const mailto = `mailto:${BUSINESS_EMAIL}?subject=${encodeURIComponent(`Coached session request: ${form.name}`)}&body=${encodeURIComponent(
    `Full name: ${form.name}\nMobile: ${form.phone}\nEmail: ${form.email}\nPreferred session: ${form.session}${form.preferred ? `\nDate and time: ${form.preferred}` : ""}`)}`;

  const field = "w-full bg-void border border-line focus:border-gold outline-none text-cream px-4 h-12 text-base";
  return (
    <Ctx.Provider value={{ open }}>
      {children}
      {show && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-void/80 backdrop-blur-sm p-0 sm:p-6" onClick={() => setShow(false)} data-testid="booking-dialog">
          <div className="w-full sm:max-w-lg bg-surface border border-gold/40 p-6 sm:p-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="booking-title">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="eyebrow">Book a coached session</p>
                <h2 id="booking-title" className="font-anton uppercase text-cream text-3xl mt-2 leading-none">Your details</h2>
              </div>
              <button onClick={() => setShow(false)} aria-label="Close" data-testid="booking-close" className="h-11 w-11 -mr-2 -mt-2 flex items-center justify-center text-smoke hover:text-gold"><X className="h-6 w-6" /></button>
            </div>

            {state === "done" ? (
              <div className="py-8 text-center" data-testid="booking-done">
                <CheckCircle2 className="h-12 w-12 text-gold mx-auto" />
                <p className="font-anton uppercase text-cream text-2xl mt-4">Request received</p>
                <p className="text-smoke mt-3">Thank you, {form.name.split(" ")[0]}. A coach will be in touch shortly to confirm <span className="text-cream">{form.session === OTHER ? form.preferred : form.session}</span>.</p>
                <button onClick={() => setShow(false)} data-testid="booking-done-button" className="mt-8 h-12 px-8 bg-gold text-void font-bold uppercase tracking-wider">Done</button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
                <label className="block">
                  <span className="text-smoke text-sm">Full name</span>
                  <input className={`${field} mt-1.5`} value={form.name} onChange={set("name")} autoComplete="name" data-testid="booking-name" />
                </label>
                <label className="block">
                  <span className="text-smoke text-sm">Mobile number</span>
                  <input className={`${field} mt-1.5`} value={form.phone} onChange={set("phone")} type="tel" inputMode="tel" autoComplete="tel" data-testid="booking-phone" />
                </label>
                <label className="block">
                  <span className="text-smoke text-sm">Email</span>
                  <input className={`${field} mt-1.5`} value={form.email} onChange={set("email")} type="email" inputMode="email" autoComplete="email" data-testid="booking-email" />
                </label>
                <label className="block">
                  <span className="text-smoke text-sm">Preferred session</span>
                  <select className={`${field} mt-1.5 appearance-none`} value={form.session} onChange={set("session")} data-testid="booking-session">
                    {ALL_SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
                    <option value={OTHER}>{OTHER}</option>
                  </select>
                </label>
                {form.session === OTHER && (
                  <label className="block">
                    <span className="text-smoke text-sm">Date and time you would like</span>
                    <input className={`${field} mt-1.5`} value={form.preferred} onChange={set("preferred")} placeholder="e.g. Wednesday 12 November, 6pm" data-testid="booking-preferred" />
                  </label>
                )}
                <input className="hidden" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} aria-hidden="true" />
                {err && <p className="text-[#E8B4A0] text-sm" role="alert">{err}</p>}
                {state === "error" && (
                  <p className="text-[#E8B4A0] text-sm" role="alert">
                    We could not send that just now. <a href={mailto} className="underline text-gold">Email it to us instead</a>.
                  </p>
                )}
                <button type="submit" disabled={state === "sending"} className="w-full h-14 bg-gold text-void hover:bg-gold-hover font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-60" data-testid="booking-submit">
                  {state === "sending" ? <Loader2 className="h-5 w-5 animate-spin" /> : "Request My Session"}
                </button>
                <p className="text-smoke text-xs text-center">Your first coached session is free. We only use your details to arrange it.</p>
              </form>
            )}
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}
